import { useState, useEffect, useCallback, useMemo } from 'react';
import { queueAPI, appointmentsAPI, patientsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Users, CheckCircle, Play, UserCheck, RefreshCw, Ban,
  Stethoscope, Phone, AlertTriangle,
} from 'lucide-react';

const STATUS_CONFIG = {
  scheduled: { label: 'Waiting', color: 'bg-blue-100 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
  checked_in: { label: 'Checked In', color: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  in_progress: { label: 'In Progress', color: 'bg-purple-100 text-purple-700 border-purple-200', dot: 'bg-purple-500' },
  completed: { label: 'Done', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  no_show: { label: 'No Show', color: 'bg-slate-100 text-slate-500 border-slate-200', dot: 'bg-slate-400' },
};

// Item 8f — Waiting card flashes red if slot has already passed (red-alert classes added in tailwind.config.js or via inline style).
const isPastSlot = (apt) => {
  if (!apt.time_slot) return false;
  const today = new Date();
  const slotDate = apt.appointment_date || today.toISOString().slice(0, 10);
  if (slotDate !== today.toISOString().slice(0, 10)) return false;
  const [h, m] = apt.time_slot.split(':').map(Number);
  const slotMins = h * 60 + m;
  const nowMins = today.getHours() * 60 + today.getMinutes();
  return slotMins < nowMins;
};

const QueueCard = ({ apt, onStatusUpdate, isDoctor }) => {
  const cfg = STATUS_CONFIG[apt.status] || STATUS_CONFIG.scheduled;
  const overdue = apt.status === 'scheduled' && isPastSlot(apt);

  // Item 2 — Doctor sees ONLY a single mandatory "Consultation Completed" button after Check-In.
  //          We removed the separate "In Progress" step for the doctor's flow because consultation
  //          end IS the only action that matters on the queue board. FD/Billing still see no
  //          action (Check-In is their last touchpoint).
  // Item 8d — "Start Consultation" never existed in this rewrite; Check-In is consultation start.
  // Item 8e — Waiting cards always show a primary "Check In" button (for FD/Billing too).
  const nextActions = {
    scheduled: [{ label: 'Check In', status: 'checked_in', icon: UserCheck, cls: 'bg-amber-500 hover:bg-amber-600 text-white' }],
    checked_in: isDoctor ? [{ label: 'Consultation Completed', status: 'completed', icon: CheckCircle, cls: 'bg-emerald-600 hover:bg-emerald-700 text-white' }] : [],
    in_progress: isDoctor ? [{ label: 'Consultation Completed', status: 'completed', icon: CheckCircle, cls: 'bg-emerald-600 hover:bg-emerald-700 text-white' }] : [],
  };
  const actions = nextActions[apt.status] || [];

  return (
    <div
      className={`p-4 rounded-xl border-2 ${cfg.color} transition-all hover:shadow-md ${overdue ? 'animate-pulse ring-2 ring-red-400 border-red-400 bg-red-50' : ''}`}
      data-testid={`queue-card-${apt.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center h-10 w-10 rounded-full bg-white/60 font-bold text-lg text-slate-700">
            #{apt.queue_number}
          </div>
          <div>
            <p className="font-semibold text-slate-900">{apt.patient_name}</p>
            <p className="text-xs text-slate-500 font-mono">{apt.patient_code}</p>
          </div>
        </div>
        <div className="text-right">
          <p className={`font-medium text-sm ${overdue ? 'text-red-700 font-bold' : ''}`}>{apt.time_slot} - {apt.slot_end_time}</p>
          <div className="flex items-center gap-1 mt-0.5">
            <div className={`h-2 w-2 rounded-full ${overdue ? 'bg-red-500' : cfg.dot}`} />
            <span className={`text-xs font-medium ${overdue ? 'text-red-700' : ''}`}>{overdue ? 'Overdue' : cfg.label}</span>
          </div>
        </div>
      </div>
      {overdue && (
        <div className="flex items-center gap-1.5 text-xs text-red-700 bg-red-100 rounded-md px-2 py-1 mb-2" data-testid={`overdue-warning-${apt.id}`}>
          <AlertTriangle size={12} /> Time slot has passed — please clear before booking the next slot.
        </div>
      )}
      {apt.chief_complaint && (
        <p className="text-sm text-slate-600 mb-3 bg-white/40 rounded-lg px-3 py-1.5">{apt.chief_complaint}</p>
      )}
      {apt.patient_phone && (
        <div className="flex items-center gap-1 text-xs text-slate-500 mb-3">
          <Phone size={11} /> {apt.patient_phone}
        </div>
      )}
      {actions.length > 0 && (
        <div className="flex gap-2">
          {actions.map(a => (
            <Button key={a.status} size="sm" className={`${a.cls} h-8 text-xs flex-1`} onClick={() => onStatusUpdate(apt.id, a.status)} data-testid={`queue-action-${a.status}-${apt.id}`}>
              <a.icon size={14} className="mr-1" /> {a.label}
            </Button>
          ))}
          {apt.status !== 'completed' && apt.status !== 'no_show' && (
            <Button size="sm" variant="ghost" className="h-8 text-xs text-red-500 hover:bg-red-50" onClick={() => onStatusUpdate(apt.id, apt.status === 'scheduled' ? 'no_show' : 'cancelled')} data-testid={`queue-cancel-${apt.id}`}>
              <Ban size={14} />
            </Button>
          )}
        </div>
      )}
      {apt.invoice_id && (
        <Badge className="mt-2 bg-emerald-50 text-emerald-600 border-emerald-200">Invoice Generated</Badge>
      )}
    </div>
  );
};

export const QueueDisplayPage = () => {
  const { user } = useAuth();
  const isDoctor = (user?.role_name || '').toLowerCase() === 'doctor' || (user?.role_code || '').toLowerCase() === 'doctor' || user?.is_super_admin;
  const [queue, setQueue] = useState([]);
  const [doctorsSummary, setDoctorsSummary] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [filterDoctor, setFilterDoctor] = useState('');
  const [loading, setLoading] = useState(true);
  // Item 7 — Per-doctor status filter. Map of doctor_id → status (or 'all').
  const [statusFilters, setStatusFilters] = useState({});

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterDoctor && filterDoctor !== 'all') params.doctor_id = filterDoctor;
      const [qRes, dRes, docRes] = await Promise.all([
        queueAPI.today(params),
        queueAPI.doctorsToday(),
        patientsAPI.getDoctors(),
      ]);
      setQueue(qRes.data);
      setDoctorsSummary(dRes.data);
      setDoctors(docRes.data);
    } catch { toast.error('Failed to load queue'); }
    finally { setLoading(false); }
  }, [filterDoctor]);

  useEffect(() => { fetch(); }, [fetch]);
  useEffect(() => { const i = setInterval(fetch, 30000); return () => clearInterval(i); }, [fetch]);

  const handleStatusUpdate = async (aptId, newStatus) => {
    try {
      await appointmentsAPI.updateStatus(aptId, { status: newStatus });
      toast.success(`Updated to ${newStatus.replace('_', ' ')}`);
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  // Group by doctor for the per-doctor sections.
  const grouped = useMemo(() => {
    const g = {};
    queue.forEach(apt => {
      const key = apt.doctor_id;
      if (!g[key]) g[key] = { name: apt.doctor_name || 'Unknown', items: [] };
      g[key].items.push(apt);
    });
    return g;
  }, [queue]);

  // Summary lookup by doctor_id
  const summaryByDoctor = useMemo(() => {
    const m = {};
    doctorsSummary.forEach(ds => { m[ds.doctor_id] = ds; });
    return m;
  }, [doctorsSummary]);

  // Item 7 — clickable status pill helper.
  const setStatusFilter = (doctorId, status) => {
    setStatusFilters(f => ({ ...f, [doctorId]: f[doctorId] === status ? null : status }));
  };

  const filteredItemsFor = (doctorId, items) => {
    const f = statusFilters[doctorId];
    if (!f) return items;
    if (f === 'waiting') return items.filter(a => a.status === 'scheduled' || a.status === 'checked_in');
    if (f === 'in_progress') return items.filter(a => a.status === 'in_progress');
    if (f === 'completed') return items.filter(a => a.status === 'completed');
    return items;
  };

  return (
    <div className="space-y-6" data-testid="queue-display-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Today&apos;s Queue</h1>
          <p className="text-sm text-slate-500 mt-1">{new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filterDoctor} onValueChange={setFilterDoctor}>
            <SelectTrigger className="w-48" data-testid="queue-filter-doctor"><SelectValue placeholder="All Doctors" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Doctors</SelectItem>
              {doctors.map(d => <SelectItem key={d.id} value={d.id}>{d.first_name} {d.last_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetch} data-testid="queue-refresh-btn"><RefreshCw size={16} /></Button>
        </div>
      </div>

      {/* Queue by Doctor */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-slate-400">Loading queue...</div>
      ) : Object.keys(grouped).length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-slate-400">
            <Users size={48} className="mb-3 opacity-40" />
            <p className="text-lg font-medium">No appointments in queue today</p>
            <p className="text-sm">Book appointments from the Appointments page</p>
          </CardContent>
        </Card>
      ) : (
        Object.entries(grouped).map(([doctorId, data]) => {
          const ds = summaryByDoctor[doctorId];
          const counts = ds || {
            waiting: data.items.filter(i => i.status === 'scheduled' || i.status === 'checked_in').length,
            in_progress: data.items.filter(i => i.status === 'in_progress').length,
            completed: data.items.filter(i => i.status === 'completed').length,
            total: data.items.length,
          };
          const activeFilter = statusFilters[doctorId];
          const shownItems = filteredItemsFor(doctorId, data.items);

          return (
            <div key={doctorId} className="space-y-3" data-testid={`doctor-section-${doctorId}`}>
              <Card className="overflow-hidden">
                <div className="h-1.5 bg-gradient-to-r from-emerald-500 to-lime-400" />
                <CardContent className="p-4">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                        <Stethoscope size={18} className="text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">Dr. {data.name}</p>
                        <p className="text-xs text-slate-500">{counts.total ?? data.items.length} appointments today</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Item 7 — Each stat is a clickable filter. Click again to clear. */}
                      <button
                        onClick={() => setStatusFilter(doctorId, 'waiting')}
                        className={`px-3 py-2 rounded-lg transition-all text-center min-w-[90px] ${activeFilter === 'waiting' ? 'bg-blue-600 text-white shadow-md ring-2 ring-blue-300' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
                        data-testid={`filter-waiting-${doctorId}`}
                      >
                        <p className="text-lg font-bold">{counts.waiting}</p>
                        <p className="text-[10px] font-medium">Waiting</p>
                      </button>
                      <button
                        onClick={() => setStatusFilter(doctorId, 'in_progress')}
                        className={`px-3 py-2 rounded-lg transition-all text-center min-w-[90px] ${activeFilter === 'in_progress' ? 'bg-purple-600 text-white shadow-md ring-2 ring-purple-300' : 'bg-purple-50 text-purple-700 hover:bg-purple-100'}`}
                        data-testid={`filter-in_progress-${doctorId}`}
                      >
                        <p className="text-lg font-bold">{counts.in_progress}</p>
                        <p className="text-[10px] font-medium">In Progress</p>
                      </button>
                      <button
                        onClick={() => setStatusFilter(doctorId, 'completed')}
                        className={`px-3 py-2 rounded-lg transition-all text-center min-w-[90px] ${activeFilter === 'completed' ? 'bg-emerald-600 text-white shadow-md ring-2 ring-emerald-300' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}
                        data-testid={`filter-completed-${doctorId}`}
                      >
                        <p className="text-lg font-bold">{counts.completed}</p>
                        <p className="text-[10px] font-medium">Completed</p>
                      </button>
                      {activeFilter && (
                        <button
                          onClick={() => setStatusFilters(f => ({ ...f, [doctorId]: null }))}
                          className="text-[10px] text-slate-400 underline hover:text-slate-600"
                          data-testid={`clear-filter-${doctorId}`}>
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {shownItems.length === 0 ? (
                <p className="text-xs text-slate-400 px-3" data-testid={`empty-${doctorId}`}>
                  No patients in {activeFilter ? activeFilter.replace('_', ' ') : 'this list'}.
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {shownItems.map(apt => (
                    <QueueCard key={apt.id} apt={apt} onStatusUpdate={handleStatusUpdate} isDoctor={isDoctor} />
                  ))}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default QueueDisplayPage;
